import { useEffect, useRef } from 'react';
import { useFileSystemStore } from '../store/useFileSystemStore';
import { useProjectStore } from '../store/useProjectStore';
import { useDebugStore } from '../store/useDebugStore';
import { fileSystemPersistence } from '../services/shared/fileSystemPersistence';
import { sanitizeFolderName, sanitizeFileName } from '../utils/textUtils';
// US-19-17 PARKED: fingerprint upgrade module exists at ./autoSaveFingerprint (Codex 5-round PASS).
// Not wired here pending US-19-19 smart recovery handshake — wiring it now would amplify upstream
// reset/recovery bugs (Codex US-19-19 4 P1: resetStaleStatuses over-eager, setFiles unsafe public API,
// recovery has no negative resolution path, run_id matching missing).
// To re-enable: import { fingerprintFile, fingerprintProject } from './autoSaveFingerprint';
//   then replace the inline pMeta + add fFp signal in currentHash JSON below.

/**
 * Hook to automatically persist ALL projects and files to local file system.
 * 
 * === Disk Format Contract ===
 * Each file is saved as multiple artifacts under project_{name}/files/:
 *   {base}.state.json          -> File metadata (no heavy data)
 *   {base}.source.txt          -> Source text (plain text)
 *   {base}.translation.txt     -> AI translation (newline-separated segments)
 *   {base}.audit.json          -> Audit result (parsed JSON, preferred)
 *   {base}.audit.csv           -> Audit raw CSV (legacy, human-readable)
 *   {base}.semantic_match.json -> Semantic match result (parsed JSON)
 *   {base}.semantic_match.csv  -> Semantic match raw CSV (human-readable)
 *   {base}.original.docx/pptx  -> Original Office binary (optional)
 *   {base}.paragraphMap.json   -> DOCX paragraph map (optional)
 * 
 * Load priority (in fileSystemPersistence.ts):
 *   audit:    .audit.json > .audit.csv > legacy .json
 *   semantic: .semantic_match.json > .semantic_match.csv > legacy
 *   state:    .state.json > legacy .json
 */
// US-19-17 PARKED 2026-04-25: Multi-tab BroadcastChannel lock attempted but Codex Stage 1
// review flagged 4 P1 issues. Reverted — proper implementation needs:
//   - stale-owner reclaim timeout (no permanent suppress on owner crash)
//   - recheck ownership inside doSave immediately before write
//   - reactive ownership state (trigger save evaluation on change)
//   - cross-origin scope (BroadcastChannel is origin-scoped; cross-origin tabs split-brain)
//   - tests for owner-crash/no-release scenarios
// Defer to dedicated story with proper test coverage.

// Fingerprint helpers extracted to ./autoSaveFingerprint for unit-testability.

export function useAutoSave() {
    const { isConnected, rootHandle } = useFileSystemStore();
    const { projects, files, deletedProjectIds } = useProjectStore();
    const lastSavedHashRef = useRef<string>('');
    const saveInProgressRef = useRef(false);
    const pendingSaveRef = useRef<(() => Promise<void>) | null>(null);

    // Flush pending save immediately on page unload (prevents data loss on refresh)
    useEffect(() => {
        const handleBeforeUnload = () => {
            if (pendingSaveRef.current) {
                // Fire-and-forget: browser may not wait, but gives save a chance
                pendingSaveRef.current();
            }
        };
        window.addEventListener('beforeunload', handleBeforeUnload);
        return () => window.removeEventListener('beforeunload', handleBeforeUnload);
    }, []);

    useEffect(() => {
        if (!isConnected || !rootHandle) {
            if (projects.length > 0 || files.length > 0) {
                console.warn(`[AutoSave] SKIPPED - not connected (isConnected=${isConnected}, hasHandle=${!!rootHandle}, projects=${projects.length}, files=${files.length})`);
            }
            return;
        }

        // Build a quick hash to detect changes
        const currentHash = JSON.stringify({
            pIds: projects.map(p => p.id).sort(),
            pMeta: projects.map(p => `${p.id}:${(p as any).synonymGroups?.length || 0}:${(p.systemPrompt || '').length}`).sort(),
            fIds: files.map(f => f.id).sort(),
            fSteps: files.map(f => `${f.id}:${f.currentStep}:${f.translationStatus}:${f.matchStatus}:${f.auditStatus}`).sort(),
            // Track data presence to trigger save when results arrive
            // Include estimatedCredits — background preflight writes here, must persist to disk
            // Include translateProgress totalSegments — fix 0/0 sau refresh khi status='running' không đổi
            fData: files.map(f => `${f.id}:${!!f.auditResult}:${!!f.semanticMatchResult}:${(f.aiOutput || []).length}:${!!f.estimatedCredits}:${f.translateProgress?.totalSegments ?? 0}:${f.translateProgress?.completedSegments ?? 0}`).sort()
        });

        // Skip if nothing changed
        if (currentHash === lastSavedHashRef.current) return;

        // Define the save function (reusable for timer + beforeunload flush)
        const doSave = async () => {
            if (saveInProgressRef.current) return;
            saveInProgressRef.current = true;

            try {
                // Ensure persistence service has the correct handle
                (fileSystemPersistence as any).handle = rootHandle;

                // Filter out blacklisted projects
                const activeProjects = projects.filter(p => !deletedProjectIds.includes(p.id));

                for (const project of activeProjects) {
                    // Determine category folder based on project type
                    let category = '';
                    if (project.type === 'translate') {
                        category = 'translating';
                    } else if (project.type === 'train') {
                        category = 'training';
                    }

                    // Build folder name
                    const projectFolderName = `project_${sanitizeFolderName(project.name, project.id)}`;

                    // Construct path: [category, project_name] or just [project_name]
                    const projectPath = category ? [category, projectFolderName] : [projectFolderName];

                    // Save project metadata (Remove full files list to avoid bloat, 
                    // it is reconstructed by the loader scanning the /files folder)
                    const { files: projectFilesList, ...projectMetadata } = project as any;
                    await fileSystemPersistence.saveJson(
                        projectPath,
                        'project.json',
                        projectMetadata
                    );

                    // Save each file individually
                    const projectFiles = files.filter(f => f.projectId === project.id);
                    // Build set of valid base names for orphan cleanup
                    const validBaseNames = new Set<string>();

                    for (const file of projectFiles) {
                        const safeName = sanitizeFileName(file.fileName);
                        const shortId = file.id.split('-')[0] || file.id.slice(0, 8);
                        const fileBaseName = `${safeName}.${shortId}`;
                        validBaseNames.add(fileBaseName);

                        const {
                            sourceText,
                            aiOutput,
                            auditResult,
                            auditRawOutput,
                            semanticMatchResult,
                            originalFile,      // ArrayBuffer - cannot JSON.stringify, omit from disk
                            docxParagraphMap,  // Large object tied to originalFile, omit from disk
                            ...lightState
                        } = file as any;

                        // 1. Save main JSON state (SLIM VERSION - metadata only)
                        // NOTE: originalFile (ArrayBuffer) and docxParagraphMap are intentionally excluded.
                        // They only live in memory for the current session. User must re-upload DOCX
                        // to get DOCX export after a page reload.
                        await fileSystemPersistence.saveJson(
                            [...projectPath, 'files'],
                            `${fileBaseName}.state.json`,
                            lightState
                        );

                        // US-19-16: PARTIAL-SAVE PROTECTION (Codex round 1 concern #2).
                        // If file metadata-only loaded (contentLoaded=false), heavy fields are placeholders.
                        // Skip heavy artifact writes to preserve disk truth — don't blank-overwrite.
                        const canWriteHeavy = file.contentLoaded === true;

                        // 2. Save readable Source text (.txt)
                        if (canWriteHeavy && sourceText) {
                            await fileSystemPersistence.saveFile(
                                [...projectPath, 'files'],
                                `${fileBaseName}.source.txt`,
                                sourceText
                            );
                        }

                        // 2b. Save original Office binary (.original.docx/.original.pptx)
                        // originalFile la ArrayBuffer, chi save khi co va hop le
                        if (canWriteHeavy && originalFile instanceof ArrayBuffer && originalFile.byteLength > 0) {
                            const originalExt = file.originalFormat === 'pptx' ? 'pptx' : 'docx';
                            await fileSystemPersistence.saveBinary(
                                [...projectPath, 'files'],
                                `${fileBaseName}.original.${originalExt}`,
                                originalFile
                            );
                        }

                        // 2c. Save DOCX paragraph map (.paragraphMap.json) for export
                        if (canWriteHeavy && docxParagraphMap && Array.isArray(docxParagraphMap) && docxParagraphMap.length > 0) {
                            await fileSystemPersistence.saveJson(
                                [...projectPath, 'files'],
                                `${fileBaseName}.paragraphMap.json`,
                                docxParagraphMap
                            );
                        }

                        // 3. Save readable AI Translation output (.txt)
                        if (canWriteHeavy && aiOutput && aiOutput.length > 0) {
                            await fileSystemPersistence.saveFile(
                                [...projectPath, 'files'],
                                `${fileBaseName}.translation.txt`,
                                aiOutput.join('\n')
                            );
                        }

                        // 4. Save Audit result
                        // 4a. Save parsed result as JSON (for reliable restore)
                        if (canWriteHeavy && auditResult) {
                            await fileSystemPersistence.saveJson(
                                [...projectPath, 'files'],
                                `${fileBaseName}.audit.json`,
                                auditResult
                            );
                        }
                        // 4b. Save raw CSV output (legacy, for human readability)
                        if (canWriteHeavy && auditRawOutput) {
                            await fileSystemPersistence.saveFile(
                                [...projectPath, 'files'],
                                `${fileBaseName}.audit.csv`,
                                auditRawOutput
                            );
                        }

                        // 5. Save Semantic Match result
                        if (canWriteHeavy && semanticMatchResult) {
                            // 5a. Save parsed result as JSON (for reliable restore)
                            await fileSystemPersistence.saveJson(
                                [...projectPath, 'files'],
                                `${fileBaseName}.semantic_match.json`,
                                {
                                    mappings: semanticMatchResult.mappings,
                                    alignedHumanSegments: semanticMatchResult.alignedHumanSegments,
                                    unmatchedCount: semanticMatchResult.unmatchedCount
                                }
                            );
                            // 5b. Save raw CSV (for human readability)
                            if (semanticMatchResult.rawOutput) {
                                await fileSystemPersistence.saveFile(
                                    [...projectPath, 'files'],
                                    `${fileBaseName}.semantic_match.csv`,
                                    semanticMatchResult.rawOutput
                                );
                            }
                        }
                    }

                    // === CLEANUP: Remove orphan files on disk ===
                    // Files whose base name doesn't match any current file in the store
                    try {
                        let filesDir: FileSystemDirectoryHandle;
                        let currentDir: FileSystemDirectoryHandle = rootHandle!;
                        for (const segment of projectPath) {
                            currentDir = await currentDir.getDirectoryHandle(segment, { create: false });
                        }
                        filesDir = await currentDir.getDirectoryHandle('files', { create: false });

                        const orphanFiles: string[] = [];
                        for await (const entry of (filesDir as any).values()) {
                            if (entry.kind !== 'file') continue;
                            // Extract base name: everything before the first known extension
                            const knownExts = ['.state.json', '.source.txt', '.translation.txt', '.audit.json', '.audit.csv', '.semantic_match.json', '.semantic_match.csv', '.original.docx', '.original.pptx', '.paragraphMap.json'];
                            let baseName = '';
                            for (const ext of knownExts) {
                                if (entry.name.endsWith(ext)) {
                                    baseName = entry.name.slice(0, -ext.length);
                                    break;
                                }
                            }
                            if (baseName && !validBaseNames.has(baseName)) {
                                orphanFiles.push(entry.name);
                            }
                        }

                        if (orphanFiles.length > 0) {
                            console.log(`[AutoSave] Cleaning ${orphanFiles.length} orphan files in ${projectPath.join('/')}/files/`);
                            for (const name of orphanFiles) {
                                try {
                                    await filesDir.removeEntry(name);
                                } catch { }
                            }
                        }
                    } catch {
                        // files/ directory doesn't exist yet or project path doesn't exist - ok
                    }
                }

                lastSavedHashRef.current = currentHash;
                console.log(`[AutoSave] Saved ${projects.length} projects, ${files.length} files to disk`);
            } catch (error) {
                console.error('[AutoSave] Failed:', error);
            } finally {
                saveInProgressRef.current = false;
                pendingSaveRef.current = null;
            }
        };

        // Store ref so beforeunload can flush immediately
        pendingSaveRef.current = doSave;

        const timer = setTimeout(doSave, 500); // 500ms debounce (reduced from 2000ms)

        return () => clearTimeout(timer);
    }, [files, projects, isConnected, rootHandle, deletedProjectIds]);
}
